import React, { useEffect, useState, useRef, useId } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'
import { XIcon } from './Icons'
import { Button } from '@/components/ui/button'
import { Text } from './Text'

export interface ModalAction {
  content: string
  onAction: () => void
  loading?: boolean
  disabled?: boolean
}

export interface ModalProps {
  /** Whether modal is open */
  open: boolean
  /** Close handler */
  onClose: () => void
  /** Modal title */
  title: string
  /** Modal subtitle */
  subtitle?: string
  /** Modal size */
  size?: 'small' | 'medium' | 'large'
  /** Modal content */
  children?: React.ReactNode
  /** Primary action button */
  primaryAction?: ModalAction
  /** Secondary action button */
  secondaryAction?: ModalAction
  /** Destructive action button */
  destructiveAction?: ModalAction
  /** Allow closing by clicking backdrop */
  backdropClose?: boolean
  /** Custom width */
  width?: string | number
  /** Data ID for testing */
  'data-id'?: string
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  size = 'medium',
  children,
  primaryAction,
  secondaryAction,
  destructiveAction,
  backdropClose = true,
  width,
  'data-id': dataId,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isRendered, setIsRendered] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const hasInitialFocusRef = useRef(false)
  const lastFocusedInputRef = useRef<HTMLElement | null>(null)

  // Track focus on input elements inside the modal
  // Note: We depend on isRendered because modalRef.current is null until the modal renders
  useEffect(() => {
    if (!open || !isRendered || !modalRef.current) return

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement
      // Save reference to focused input/textarea elements
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        lastFocusedInputRef.current = target
      }
    }

    const handleFocusOut = (e: FocusEvent) => {
      const relatedTarget = e.relatedTarget as HTMLElement | null
      const target = e.target as HTMLElement

      // If focus is leaving an input and going to a button (likely the close button)
      // restore focus to the input. We query for the current input element because
      // React may have remounted it during re-render.
      if (
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') &&
        relatedTarget?.tagName === 'BUTTON' &&
        modalRef.current
      ) {
        // Use setTimeout to allow React to fully complete its update cycle
        setTimeout(() => {
          // Find the current input element (may be a new element after React re-render)
          const currentInput = modalRef.current?.querySelector('input:not([type="checkbox"]), textarea') as HTMLElement
          if (currentInput && document.activeElement !== currentInput) {
            currentInput.focus()
          }
        }, 0)
      }
    }

    const modal = modalRef.current
    modal.addEventListener('focusin', handleFocusIn)
    modal.addEventListener('focusout', handleFocusOut)

    return () => {
      modal.removeEventListener('focusin', handleFocusIn)
      modal.removeEventListener('focusout', handleFocusOut)
    }
  }, [open, isRendered])

  // Handle mounting/unmounting with animation
  useEffect(() => {
    if (open) {
      setIsRendered(true)
      const timer = setTimeout(() => setIsVisible(true), 10)
      return () => clearTimeout(timer)
    } else {
      setIsVisible(false)
      hasInitialFocusRef.current = false // Reset for next open
      const timer = setTimeout(() => setIsRendered(false), 200)
      return () => clearTimeout(timer)
    }
  }, [open])

  // Store onClose in a ref to avoid stale closures while keeping effect stable
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Focus trap and Escape key
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current()
      }
      // Simple focus trap
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        const firstElement = focusableElements[0] as HTMLElement
        const lastElement = focusableElements[
          focusableElements.length - 1
        ] as HTMLElement

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault()
            lastElement.focus()
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault()
            firstElement.focus()
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    // Focus first element only on initial open (not on every re-render)
    let timer: ReturnType<typeof setTimeout> | undefined
    if (!hasInitialFocusRef.current) {
      timer = setTimeout(() => {
        if (modalRef.current) {
          const firstFocusable = modalRef.current.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          ) as HTMLElement
          if (firstFocusable) {
            firstFocusable.focus()
          } else {
            modalRef.current.focus()
          }
          hasInitialFocusRef.current = true
        }
      }, 50)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
      if (timer) clearTimeout(timer)
    }
  }, [open])

  if (!isRendered) return null

  const sizeClasses = {
    small: 'max-w-[400px]',
    medium: 'max-w-[600px]',
    large: 'max-w-[800px]',
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (backdropClose && e.target === e.currentTarget) {
      onClose()
    }
  }

  const hasFooter = primaryAction || secondaryAction || destructiveAction

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 transition-all duration-200 ease-out',
        isVisible ? 'visible' : 'invisible'
      )}
      data-id={dataId}
    >
      {/* Backdrop */}
      <div
        className={cn(
          'absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ease-out',
          isVisible ? 'opacity-100' : 'opacity-0'
        )}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'relative w-full bg-background rounded-xl shadow-[0_26px_80px_rgba(0,0,0,0.2),0_0_1px_rgba(0,0,0,0.2)]',
          'transform transition-all duration-200 ease-out flex flex-col max-h-[90vh]',
          isVisible
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 translate-y-4',
          sizeClasses[size]
        )}
        style={width ? { maxWidth: width } : undefined}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-4 border-b border-border">
          <div className="flex-1 min-w-0">
            <Text
              as="h2"
              size="heading-small"
              spacing="none"
              id={titleId}
            >
              {title}
            </Text>
            {subtitle && (
              <Text size="small" color="subdued" spacing="none" className="mt-1">
                {subtitle}
              </Text>
            )}
          </div>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-accent-foreground hover:bg-accent rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Fechar"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>

        {/* Footer */}
        {hasFooter && (
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
            {destructiveAction && (
              <Button
                type="button"
                variant="destructive"
                onClick={destructiveAction.onAction}
                disabled={destructiveAction.disabled || destructiveAction.loading}
              >
                {destructiveAction.loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {destructiveAction.content}
              </Button>
            )}
            <div className="flex-1" />
            {secondaryAction && (
              <Button
                type="button"
                variant="outline"
                onClick={secondaryAction.onAction}
                disabled={secondaryAction.disabled || secondaryAction.loading}
              >
                {secondaryAction.loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {secondaryAction.content}
              </Button>
            )}
            {primaryAction && (
              <Button
                type="button"
                onClick={primaryAction.onAction}
                disabled={primaryAction.disabled || primaryAction.loading}
              >
                {primaryAction.loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {primaryAction.content}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
